import React, { useState } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { CountdownTimer } from './CountdownTimer';
import { ShieldCheck, Zap, Lock, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
interface PricingOption {
  id: string;
  emoji: string;
  name: string;
  description: string;
  oldPrice: string;
  price: string;
  highlighted: boolean;
  savings?: string;
}
const options: PricingOption[] = [{
  id: 'standard',
  emoji: '📘',
  name: 'GIMNASIA ARTÍSTICA RESUELTA',
  description: '6 Tomos + 60 clases por aparato + 6 bonos',
  oldPrice: '$34.990',
  price: '$19.990',
  highlighted: false
}, {
  id: 'elite',
  emoji: '🏆',
  name: 'PACK ÉLITE™',
  description: '6 Tomos + 6 Bonos + Coreografías + Academia',
  oldPrice: '$43.990',
  price: '$27.990',
  highlighted: true,
  savings: 'AHORRÁS $16.000'
}];
const CHECKOUT_URLS: Record<string, string> = {
  standard: 'https://pagos.recursosfitness.shop/cart/44084608991329:1',
  elite: 'https://pagos.recursosfitness.shop/cart/44084609417313:1'
};
export function PricingSection() {
  const [selected, setSelected] = useState('elite');
  return <Section id="pricing" data-id="element-219">
      <div className="text-center mb-4 md:mb-10" data-id="element-220">
        <h2 className="text-xl sm:text-3xl md:text-5xl font-black text-white mb-1 md:mb-4" data-id="element-221">
          ELEGÍ TU GIMNASIA ARTÍSTICA RESUELTA
        </h2>
        <p className="text-xs md:text-xl text-gray-400" data-id="element-222">
          Las dos opciones incluyen los 6 bonos completos
        </p>
      </div>

      <CountdownTimer data-id="element-223" />

      <div className="max-w-lg mx-auto mt-4 md:mt-8 space-y-2 md:space-y-3" data-id="element-224">
        {options.map(opt => {
        const isSelected = selected === opt.id;
        return <button key={opt.id} onClick={() => setSelected(opt.id)} className={`w-full text-left rounded-xl md:rounded-2xl p-3 md:p-5 border-2 transition-all duration-200 relative ${opt.highlighted ? isSelected ? 'border-gold-500 bg-navy-800 shadow-[0_0_20px_rgba(212,168,67,0.15)]' : 'border-gold-500/40 bg-navy-800/60' : isSelected ? 'border-navy-600 bg-navy-800' : 'border-navy-700 bg-navy-900'}`} data-id="element-225">
              {/* Highlighted badges */}
              {opt.highlighted && <div className="flex items-center gap-1.5 mb-2 md:mb-3" data-id="element-226">
                  <span className="bg-gold-500 text-navy-900 text-[9px] md:text-xs font-black px-2 py-0.5 rounded-full uppercase" data-id="element-227">
                    ⭐ Más elegido
                  </span>
                  {opt.savings && <span className="bg-gold-500/15 text-gold-400 text-[9px] md:text-xs font-bold px-2 py-0.5 rounded-full border border-gold-500/30" data-id="element-228">
                      {opt.savings}
                    </span>}
                </div>}

              <div className="flex items-center justify-between" data-id="element-229">
                <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-230">
                  {/* Radio circle */}
                  <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-gold-400' : 'border-navy-600'}`} data-id="element-231">
                    {isSelected && <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-gold-400" data-id="element-232" />}
                  </div>

                  <div className="min-w-0" data-id="element-233">
                    <p className="text-white font-bold text-sm md:text-lg flex items-center gap-1.5" data-id="element-234">
                      <span data-id="element-235">{opt.emoji}</span>
                      <span className="truncate" data-id="element-236">{opt.name}</span>
                    </p>
                    <p className="text-gray-400 text-[10px] md:text-sm truncate" data-id="element-237">
                      {opt.description}
                    </p>
                  </div>
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0 ml-3" data-id="element-238">
                  <p className="text-gray-500 line-through text-[10px] md:text-sm" data-id="element-239">
                    {opt.oldPrice}
                  </p>
                  <p className={`text-xl md:text-3xl font-black ${opt.highlighted ? 'text-gold-400' : 'text-white'}`} data-id="element-240">
                    {opt.price}
                  </p>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* CTA */}
      <div className="max-w-lg mx-auto mt-4 md:mt-6" data-id="element-241">
        <motion.a href={CHECKOUT_URLS[selected]} target="_blank" rel="noopener noreferrer" whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="block w-full text-center py-3.5 md:py-5 text-sm md:text-xl bg-magenta-500 hover:bg-magenta-600 text-white font-bold px-5 sm:px-8 rounded-xl shadow-[0_0_20px_rgba(233,30,140,0.4)] hover:shadow-[0_0_30px_rgba(233,30,140,0.6)] transition-all duration-300 uppercase tracking-wide" data-id="element-242">
          SÍ, QUIERO MI GIMNASIA ARTÍSTICA RESUELTA →
        </motion.a>
        <p className="text-center text-[10px] md:text-sm text-gray-400 mt-2 italic" data-id="element-243">
          La opción más elegida por profesores de artística
        </p>
      </div>

      {/* Trust Badges */}
      <div className="flex flex-wrap items-center justify-center gap-2 md:gap-8 mt-4 md:mt-8 pt-3 md:pt-6 border-t border-navy-800 max-w-lg mx-auto" data-id="element-244">
        {[{
        icon: Lock,
        text: 'Pago seguro'
      }, {
        icon: Zap,
        text: 'Acceso en 5 min'
      }, {
        icon: ShieldCheck,
        text: 'Garantía 7 días'
      }, {
        icon: CreditCard,
        text: 'Mercado Pago'
      }].map((item, i) => <div key={i} className="flex items-center space-x-1 text-gray-400 text-[10px] md:text-sm" data-id="element-245">
            <item.icon className="w-3 h-3 md:w-4 md:h-4 text-gold-400" data-id="element-246" />
            <span className="font-medium" data-id="element-247">{item.text}</span>
          </div>)}
      </div>
    </Section>;
}